import { Dialog, DialogActions, DialogContent, DialogTitle, FormControl, FormControlLabel, FormGroup, Grid, Switch, TextField, Typography } from '@mui/material';
import { withStyles, WithStyles } from "@mui/styles";
import * as api from "api/Core";
import * as kapi from "api/Knowledge";
import styles from "assets/jss/cardCreation.styles";
import { AppMessageBox } from 'components/AppMessageBox';
import IconButtonClose from 'components/Buttons/IconButtonClose';
import { RequestIndicator } from 'components/CallServer';
import RequestErrorDialog from 'components/CallServer/RequestErrorDialog';
import { RequestErrorReport } from 'components/CallServer/RequestErrorReport';
import AttachSelection, { ConvertUploadToFileThumbnaildGetDto, UploadingStatus } from 'components/FileUpload/AttachSelection';
import IconTrophy from 'components/Icons/IconTrophy';
import MuiRegularButton from 'components/MuiExtras/Button/MuiRegularButton';
import MuiKeyboardDatePicker from 'components/MuiExtras/MuiKeyboardDatePicker';
import { withSnackbar, WithSnackbar } from 'components/SnackbarProvider/Snackbar';
import TextFieldCounter from 'components/TextFieldCounter';
import TextFieldNumeric from 'components/TextFieldNumeric';
import MediaUtils from 'lib/MediaUtils';
import { AspectRatio, PublicationEditPermissions, SystemRoles } from 'models/ApiEnums';
import { AsyncUploadModel, ContentMetadata, FileThumbnaildGetDto, GetCourseDto, SaveCourseDto, SavePublicationResult } from 'models/ApiModels';
import { AttachType, RequestLifeCycle } from 'models/AppModels';
import { Moment } from "moment";
import * as React from 'react';
import { WithTranslation, withTranslation } from 'react-i18next';
import TopicsAndMembersBox from 'views/Publication/Components/TopicsAndMembersBox';

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

const MaxChars = 1500;

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

interface IProps {
    publicationId?: string;
    buttonCloseText?: string;
    onClose: (result: SavePublicationResult) => void;
}

interface IValidation {
    title?: boolean;
    topics?: boolean;
    period?: boolean;
}

interface IState {
    record: GetCourseDto;
    validation: IValidation;

    step: number;
    selectedAttach?: AttachType;
    formCycle: RequestLifeCycle;
    uploadingStatus: UploadingStatus;
    lastError: any;
    updatePoints: boolean;
    oldGainPoints: number;
}

type Props = IProps & WithStyles<typeof styles> & WithTranslation & WithSnackbar;

///////////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

class CreateCourse extends React.Component<Props, IState> {

    constructor(props: Props) {
        super(props);
        this.state = {
            record: {
                publicationId: null,
                contentBody: "",
                title: "",
                topics: [],
                relativesTopics: [],
                deletedRelativesTopics: [],
                attachs: null,

                viewPeriodStart: null,
                viewPeriodEnd: null,

                gainPoints: null,
                autoEnroll: false,
                reviewAfterChanged: false,
                viewAllVideo: false,

                editPermissions: PublicationEditPermissions.Full
            },
            validation: {
                title: null,
                topics: null,
                period: true,
            },

            selectedAttach: null,
            step: 1,
            formCycle: RequestLifeCycle.Loading,
            uploadingStatus: "none",
            lastError: null,
            updatePoints: false,
            oldGainPoints: 0
        };
    }

    ///
    private updateRecord(data: Partial<GetCourseDto>) {
        return { ...this.state.record, ...data };
    }

    ///
    private updatePeriodValidation(record: GetCourseDto) {
        return {
            ...this.state.validation,
            period: record.viewPeriodStart == null || record.viewPeriodEnd == null || record.viewPeriodStart < record.viewPeriodEnd
        };
    }

    ///
    private handleChange(event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) {
        var v = event.target.value;
        switch (event.target.name) {
            case "title":
                this.setState({
                    record: this.updateRecord({ title: v }),
                    validation: { ...this.state.validation, title: v.length > 0 }
                });
                break;
        }
    }

    ///
    private handleFileUploaded(e: AsyncUploadModel) {
        var data: FileThumbnaildGetDto = ConvertUploadToFileThumbnaildGetDto(e);
        var at = MediaUtils.getAttachTypeFromMimeType(data.mainFile.mimeType);

        if (at == AttachType.Image) {
            const files = this.state.record.attachs == null ? [] : this.state.record.attachs;
            files.push(data);
            this.setState({
                selectedAttach: AttachType.Image,
                record: this.updateRecord({ attachs: files })
            });
        }
        else if (at == AttachType.Video)
            this.setState({
                selectedAttach: AttachType.Video,
                record: this.updateRecord({ attachs: [data] })
            });
    }

    ///
    private handleUploadRemove(e: FileThumbnaildGetDto | ContentMetadata) {

        if ((e as FileThumbnaildGetDto).mainFile) {
            this.setState({
                selectedAttach: null,
                record: this.updateRecord({ attachs: null })
            });
        }
    }

    ///
    private handleUploadingStatus = (status: UploadingStatus) => {
        this.setState({ uploadingStatus: status });
    };

    ///
    private init() {

        api.getCourseRecordFormConfig()
            .then((config) => {
                if (this.props.publicationId != null) {
                    this.load(this.props.publicationId);
                }
                else {
                    this.setState((state) => {

                        state.record.gainPoints = config.courseCompletePoints;

                        return { ...state, formCycle: RequestLifeCycle.None };
                    });
                }
            })
            .catch((error) => {
                this.setState({ formCycle: RequestLifeCycle.LoadFailed });
            });
    }

    ///
    private load(publicationId: string) {

        kapi.getCourse(publicationId)
            .then((record) => {

                var selectedAttach = null;
                if (record.attachs.length > 0) {
                    selectedAttach = MediaUtils.getAttachTypeFromMimeType(record.attachs[0].mainFile.mimeType);
                }

                this.setState({
                    formCycle: RequestLifeCycle.None,
                    record,
                    selectedAttach,
                    step: record.editPermissions == PublicationEditPermissions.None ? 0 : 1,
                    validation: {
                        topics: true,
                        title: true,
                        period: true,
                    },
                    oldGainPoints: record.gainPoints
                });
            })
            .catch((error) => {
                this.setState({ lastError: error, formCycle: RequestLifeCycle.LoadFailed });
            });
    }

    ///
    private save() {
        const { record } = this.state;

        const dto: SaveCourseDto = {
            publicationId: record.publicationId,
            title: record.title,
            contentBody: record.contentBody,
            attachs: record.attachs
                ? record.attachs.map((v) => {
                    return { fileId: v.mainFile.fileId, thumbnailId: v.thumbnail == null ? null : v.thumbnail.fileId };
                })
                : null,
            topics: record.topics.filter(p => !p.fixed).map((v) => { return v.key; }),
            relativesTopics: record.relativesTopics?.map((v) => { return v.key; }),

            viewPeriodStart: record.viewPeriodStart,
            viewPeriodEnd: record.viewPeriodEnd,

            gainPoints: record.gainPoints,
            autoEnroll: record.autoEnroll,
            reviewAfterChanged: record.reviewAfterChanged,
            viewAllVideo: record.viewAllVideo,
            updatePoints: this.state.updatePoints
        };

        this.setState({ formCycle: RequestLifeCycle.Posting });

        kapi.saveCourse(dto)
            .then((result) => {
                this.props.onClose(result);
            })
            .catch((error) => {
                this.setState({ lastError: error, formCycle: RequestLifeCycle.PostFailed });
            });
    }

    ///
    private canGoStep2(): boolean {
        return this.state.validation.title;
    }

    ///
    private canSave(): boolean {
        const { validation } = this.state;

        const result =
            validation.title
            && this.state.uploadingStatus != "uploading"
            && validation.topics
            && validation.period === true;

        return result;
    }

    ///
    private loadPrediction() {
        if (this.state.step != 2) return;

        const { title, contentBody, publicationId, topics } = this.state.record;
        if (publicationId != null || topics?.length > 0) return;

        this.setState({ formCycle: RequestLifeCycle.Loading },
            () => {
                kapi.getTopicPredictions(title, contentBody)
                    .then((result) => {
                        if (result) {
                            this.setState({
                                formCycle: RequestLifeCycle.None,
                                record: this.updateRecord({ topics: result.topics, relativesTopics: result.relativesTopics, deletedRelativesTopics: [] }),
                                validation: { ...this.state.validation, topics: true }
                            }, () => result?.topics.length > 0 && this.props.snackbar.showMessage(this.props.t("Views.Publication.Common.Form.TopicSuggestionFound"), "info"));
                        }
                        else {
                            this.setState({
                                formCycle: RequestLifeCycle.None
                            });
                        }
                    })
                    .catch((error) => {
                        this.setState({
                            formCycle: RequestLifeCycle.None
                        });
                    });
            });
    }

    ///
    private goStep1() {
        this.setState({ step: 1 });
    }

    ///
    private goStep2() {
        if (this.canGoStep2()) {
            this.setState({
                step: this.state.step + 1
            }, () => this.loadPrediction());
        }
    }

    ///
    private step1() {
        const { classes } = this.props;
        const { record, validation } = this.state;
        const { editPermissions } = record;

        return (
            <>
                <DialogContent className={classes.dialogContent}>

                    <div className={classes.container}>

                        <div>

                            <div className={classes.inputBlock}>
                                <TextField
                                    autoFocus
                                    fullWidth
                                    name="title"
                                    placeholder={this.props.t("Views.Publication.CreateCourse.TitlePlaceholder")}
                                    autoComplete="off"
                                    value={record.title}
                                    error={validation.title === false}
                                    inputProps={{ maxLength: 255 }}
                                    InputProps={{ readOnly: editPermissions != PublicationEditPermissions.Full }}
                                    onChange={(e) => { this.handleChange(e); }}
                                />
                            </div>

                            <Grid
                                container
                                spacing={3}
                                direction="row"
                            >
                                <Grid item>

                                    <AttachSelection
                                        acceptedTypes={[AttachType.Image, AttachType.Video]}
                                        aspectRatio={AspectRatio.Wide}
                                        attach={record.attachs}
                                        dropZoneText={this.props.t("Views.Publication.CreateCourse.DropzoneText")}
                                        onUploadComplete={(e) => this.handleFileUploaded(e)}
                                        onUploadRemove={(e) => this.handleUploadRemove(e)}
                                        onUploadStatusChange={this.handleUploadingStatus}
                                    />

                                </Grid>
                                <Grid item style={{ flexGrow: 1 }}>
                                    <TextFieldCounter
                                        fullWidth
                                        multiline
                                        placeholder={this.props.t("Views.Publication.CreateCourse.DescriptionPlaceholder")}
                                        value={record.contentBody}
                                        maxLength={MaxChars}
                                        readOnly={editPermissions != PublicationEditPermissions.Full}
                                        rows={4}
                                        onChange={
                                            (v) => {
                                                this.setState({
                                                    record: this.updateRecord({ contentBody: v })
                                                });
                                            }
                                        }
                                    />
                                </Grid>
                            </Grid>

                        </div>
                    </div>
                </DialogContent>

                <DialogActions>

                    {
                        this.state.record.publicationId == null &&
                        <div className={classes.footerMessage}>
                            <b>{this.props.t("Views.Publication.CreateCourse.Footer1")}</b>
                            <span>&nbsp;&raquo;&nbsp;</span>
                            <span>{this.props.t("Views.Publication.Common.Form.Footer2")}</span>
                        </div>
                    }

                    <MuiRegularButton
                        variant="contained"
                        color="primary"
                        disabled={!this.canGoStep2()}
                        onClick={() => this.goStep2()}
                    >
                        <span>{this.props.t("Views.Publication.Common.Form.ButtonContinueText")}</span>
                    </MuiRegularButton>
                </DialogActions>
            </>
        );
    }

    private step2() {
        const { classes } = this.props;
        const { record, validation } = this.state;
        return (
            <>
                <DialogContent>
                    <div className={classes.container}>

                        <TopicsAndMembersBox
                            label={this.props.t<string>("Views.Publication.CreateCourse.TopicLabel")}
                            skipTopicAccessFor={SystemRoles.CourseManager}
                            topics={record.topics}
                            relativesTopics={record.relativesTopics}
                            deletedRelativesTopics={record.deletedRelativesTopics}
                            findRelativesTopics={record.publicationId == null}
                            onChange={
                                (topics, relativesTopics, deletedRelativesTopics) => {
                                    this.setState({
                                        record: this.updateRecord({ topics, relativesTopics, deletedRelativesTopics }),
                                        validation: { ...this.state.validation, ...{ topics: topics.length > 0 } }
                                    });
                                }
                            }
                        />

                        <Grid container spacing={1}>

                            <Grid item xs={6}>
                                <MuiKeyboardDatePicker
                                    fullWidth
                                    clearable
                                    error={validation.period == false}
                                    margin="dense"
                                    label="Disponível a partir de"
                                    InputLabelProps={{ shrink: true }}
                                    placeholder="Imediatamente"
                                    value={record.viewPeriodStart == null ? null : record.viewPeriodStart}
                                    onChange={
                                        (date: Moment) => {
                                            let newRecord = this.state.record;

                                            if (date == null) {
                                                newRecord = this.updateRecord({ viewPeriodStart: null });
                                            }
                                            else {
                                                date.set({ hour: 0, minute: 0, second: 0 });
                                                newRecord = this.updateRecord({ viewPeriodStart: date.toDate() });
                                            }

                                            this.setState({ record: newRecord });
                                        }
                                    }
                                    onBlur={
                                        () => {
                                            this.setState({
                                                validation: this.updatePeriodValidation(this.state.record)
                                            });
                                        }
                                    }
                                />
                            </Grid>

                            <Grid item xs={6}>
                                <MuiKeyboardDatePicker
                                    fullWidth
                                    clearable
                                    error={validation.period == false}
                                    margin="dense"
                                    label={this.props.t<string>("Views.Publication.Common.Form.AvailableUntilLabel")}
                                    placeholder={this.props.t("Common.Literals.DateUnlimited")}
                                    InputLabelProps={{ shrink: true }}
                                    value={record.viewPeriodEnd == null ? null : record.viewPeriodEnd}
                                    onChange={
                                        (date: Moment) => {
                                            let newRecord = this.state.record;

                                            if (date == null) {
                                                newRecord = this.updateRecord({ viewPeriodEnd: null });
                                            }
                                            else {
                                                date.set({ hour: 23, minute: 59, second: 59 });
                                                newRecord = this.updateRecord({ viewPeriodEnd: date.toDate() });
                                            }
                                            this.setState({ record: newRecord });
                                        }
                                    }
                                    onBlur={
                                        () => {
                                            this.setState({
                                                validation: this.updatePeriodValidation(this.state.record)
                                            });
                                        }
                                    }
                                />
                            </Grid>
                        </Grid>

                        <FormControl fullWidth margin="normal">
                            <Grid container alignContent="center" alignItems="center" spacing={2}>
                                <Grid item>
                                    <IconTrophy color="muted" fixedWidth size="2x" />
                                </Grid>
                                <Grid item>
                                    <TextFieldNumeric
                                        fullWidth
                                        name="gainPoints"
                                        allowNegative={false}
                                        placeholder={this.props.t("Views.Publication.Common.Form.GainPointsPlaceholder")}
                                        label={this.props.t<string>("Views.Publication.Common.Form.GainPointsLabel")}
                                        value={record.gainPoints}
                                        onChange={
                                            (e) => {
                                                const v = (e.target as HTMLInputElement).valueAsNumber;
                                                this.setState({
                                                    record: this.updateRecord({ gainPoints: v == 0 ? null : Math.trunc(v) })
                                                });
                                            }
                                        }
                                    />
                                </Grid>
                            </Grid>
                        </FormControl>

                        <FormGroup row>
                            <FormControlLabel
                                control={
                                    <Switch
                                        name="autoEnroll"
                                        checked={record.autoEnroll}
                                        onChange={
                                            (e) => {
                                                this.setState({ record: this.updateRecord({ autoEnroll: (e.target as HTMLInputElement).checked }) });
                                            }
                                        }
                                        value="autoEnroll"
                                        color="secondary"
                                    />
                                }
                                label={this.props.t<string>("Views.Publication.CreateCourse.AutoEnrollLabel")}
                            />
                        </FormGroup>

                        <FormGroup row>
                            <FormControlLabel
                                control={
                                    <Switch
                                        name="videoStartToEnd"
                                        checked={record.viewAllVideo}
                                        onChange={
                                            (e) => {
                                                this.setState({ record: this.updateRecord({ viewAllVideo: (e.target as HTMLInputElement).checked }) });
                                            }
                                        }
                                        value="videoStartToEnd"
                                        color="secondary"
                                    />
                                }
                                label={this.props.t<string>("Views.Publication.CreateCourse.VideoLabel")}
                            />
                        </FormGroup>

                        <FormGroup row>
                            <FormControlLabel
                                control={
                                    <Switch
                                        name="reviewAfterContentChanged"
                                        checked={record.reviewAfterChanged}
                                        onChange={
                                            (e) => {
                                                this.setState({ record: this.updateRecord({ reviewAfterChanged: (e.target as HTMLInputElement).checked }) });
                                            }
                                        }
                                        value="reviewAfterContentChanged"
                                        color="secondary"
                                    />
                                }
                                label={this.props.t<string>("Views.Publication.CreateCourse.ReviewLabel")}
                            />
                        </FormGroup>

                        { record.publicationId != null &&
                            <FormGroup row>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            name="updatePoints"
                                            checked={this.state.updatePoints}
                                            disabled={this.state.oldGainPoints == record.gainPoints}
                                            onChange={
                                                (e) => {
                                                    this.setState({ updatePoints: (e.target as HTMLInputElement).checked});
                                                }
                                            }
                                            value="autoEnroll"
                                            color="secondary"
                                        />
                                    }
                                    label={this.props.t<string>("Views.Publication.CreateCourse.UpdateGainPoint")}
                                />
                            </FormGroup>
                        }
                    </div>

                </DialogContent>

                <DialogActions>
                    <MuiRegularButton variant="contained" onClick={() => this.goStep1()} >
                        <span>{this.props.t("Views.Publication.Common.Form.ButtonBackText")}</span>
                    </MuiRegularButton>

                    <MuiRegularButton
                        variant="contained"
                        color="primary"
                        style={{ marginLeft: "auto" }}
                        onClick={() => this.save()}
                        disabled={!this.canSave()}
                    >
                        <span>{this.props.buttonCloseText || this.props.t("Views.Publication.Common.Form.ButtonAddItemText")}</span>
                    </MuiRegularButton>
                </DialogActions>
            </>
        );
    }

    ///
    public componentDidMount() {
        this.init();
    }

    ///
    public render() {
        const { formCycle, lastError, step } = this.state;
        return (
            <Dialog
                open={true}
                maxWidth={"md"}
                fullWidth
            >
                <DialogTitle>
                    <Grid container justifyContent="space-between">
                        <Grid item>
                            <Typography variant="h6">
                                {this.props.t(this.props.publicationId ? "Views.Publication.CreateCourse.TitleEdit" : "Views.Publication.CreateCourse.TitleNew")}
                            </Typography>
                        </Grid>
                        <Grid item>
                            <IconButtonClose
                                color="inherit"
                                size="small"
                                onClick={() => this.props.onClose(null)}
                            />
                        </Grid>
                    </Grid>
                </DialogTitle>

                {
                    formCycle == RequestLifeCycle.LoadFailed
                        ? (
                            <>
                                <DialogContent className={this.props.classes.dialogContent}>
                                    <RequestErrorReport error={lastError} />;
                                </DialogContent>
                                <DialogActions />
                            </>
                        )
                        : step == 0
                            ? (
                                <>
                                    <DialogContent className={this.props.classes.dialogContent}>
                                        <AppMessageBox resource="CourseEditPermissionDenied" />
                                    </DialogContent>
                                    <DialogActions />
                                </>
                            )
                            : step == 1
                                ? this.step1()
                                : step == 2
                                    ? this.step2()
                                    : null
                }

                <RequestIndicator type="request" open={formCycle == RequestLifeCycle.Loading} />
                <RequestIndicator type="post" open={formCycle == RequestLifeCycle.Posting} />

                <RequestErrorDialog
                    open={formCycle == RequestLifeCycle.PostFailed}
                    error={lastError}
                    onClose={() => this.setState({ lastError: null, formCycle: RequestLifeCycle.None })}
                />

            </Dialog>
        );
    }
}

export default withSnackbar(withTranslation()(withStyles(styles)(CreateCourse)));